import React, { useRef, useState } from "react";
import { Icon } from "../icons";
import { fmtDate, fmtDateTime } from "../lib/format";
import { buildCopyPasteText, buildNotesSummaryText, notesByStage } from "../lib/notesHub";
import { RISK_NOTE_KEYS, suggestCrewSize } from "../lib/selectors";
import { CoiBadge, ReadField, ZohoBadgeVal } from "./fields";

/** Copies a textarea's text; falls back to execCommand, then to asking the user. */
const useCopy = (taRef) => {
  const [copyLabel, setCopyLabel] = useState(null);
  const copy = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    let copied = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(ta.value);
        copied = true;
      }
    } catch (err) {
      copied = false;
    }
    if (!copied) {
      try {
        document.execCommand("copy");
        copied = true;
      } catch (err) {
        copied = false;
      }
    }
    setCopyLabel(copied ? "Copied!" : "Select the text above and copy");
    setTimeout(() => setCopyLabel(null), 1800);
  };
  return [copy, copyLabel];
};

/** Plain-text summary of every stage's notes, ready to paste into Zoho or a message. */
const NotesSummary = ({ job, isLD }) => {
  const taRef = useRef(null);
  const [copy, copyLabel] = useCopy(taRef);
  return (
    <div className="panel notes-summary" style={{ marginBottom: 12 }}>
      <div className="panel-head">
        <Icon name="clipboard" />
        Copy-Paste Summary — all stages
      </div>
      <div className="panel-body" style={{ maxHeight: "none" }}>
        <textarea
          ref={taRef}
          readOnly
          rows={12}
          style={{ fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", fontSize: 11, lineHeight: 1.5 }}
          value={buildNotesSummaryText(job, isLD)}
        />
        <div className="toolbar-row">
          <button className="btn primary small" onClick={copy}>
            <Icon name={copyLabel === "Copied!" ? "check" : "note"} />
            {copyLabel || "Copy to clipboard"}
          </button>
        </div>
      </div>
    </div>
  );
};

const NotesFeed = ({ job, isLD, actions }) => {
  const [note, setNote] = useState("");
  const groups = notesByStage(job, isLD);
  return (
    <div className="notes-hub">
      <div className="section-label" style={{ margin: "0 0 4px 0" }}>
        <Icon name="note" />
        Notes Hub
      </div>
      {groups.length ? (
        groups.map((g) => (
          <div className="notes-stage" key={g.key}>
            <div className="notes-stage-head">{g.label}</div>
            {g.notes.map((n, i) => (
              <div className="note-item" style={{ marginBottom: 4 }} key={i}>
                {g.key !== "general" ? (
                  <span className="badge plain" style={{ marginRight: 6 }}>
                    {n.label}
                  </span>
                ) : null}
                {n.text}
                {n.author || n.date ? (
                  <div className="meta">
                    {n.author}
                    {n.date ? " · " + fmtDateTime(n.date) : ""}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ))
      ) : (
        <div className="panel-empty" style={{ padding: "8px 4px" }}>
          No notes yet.
        </div>
      )}
      <textarea
        placeholder="Leave a note (syncs to Zoho, status unchanged)"
        style={{ marginTop: 2 }}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if (note.trim()) {
            actions.addNote(job.id, note.trim());
            setNote("");
          }
        }}
      />
      <div className="toolbar-row">
        <button
          className="btn"
          onClick={() => {
            if (!note.trim()) return;
            actions.addNote(job.id, note.trim());
            setNote("");
          }}
        >
          <Icon name="note" />
          Add note
        </button>
      </div>
      <NotesSummary job={job} isLD={isLD} />
    </div>
  );
};

/** Everything the MC captured on the calls plus the dispatch brief, as one block to paste into Zoho. */
const CopyPasteVersion = ({ job, isLD }) => {
  const taRef = useRef(null);
  const [copy, copyLabel] = useCopy(taRef);
  return (
    <div className="panel copy-paste-version" style={{ marginBottom: 12 }}>
      <div className="panel-head">
        <Icon name="clipboard" />
        Copy-Paste Version — for Zoho Dispatch Notes
      </div>
      <div className="panel-body" style={{ maxHeight: "none" }}>
        <textarea
          ref={taRef}
          readOnly
          rows={16}
          style={{
            fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace",
            fontSize: 11,
            lineHeight: 1.5,
          }}
          value={buildCopyPasteText(job, isLD)}
        />
        <div className="toolbar-row">
          <button className="btn primary small" onClick={copy}>
            <Icon name={copyLabel === "Copied!" ? "check" : "note"} />
            {copyLabel || "Copy to clipboard"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DispatchBrief = ({ job }) => {
  const crewEst = suggestCrewSize(job.totalCubicFeet);
  const s2 = (k) => (job.s2Notes && job.s2Notes[k]) || "";

  return (
    <>
      <div className="section-label" style={{ margin: "0 0 4px 0" }}>
        <Icon name="clipboard" />
        Dispatch &amp; Planning Brief{" "}
        <span
          className="meta"
          style={{
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: 0,
            color: "var(--ink-400)",
          }}
        >
          — confirmed details for crew assignment
        </span>
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <Icon name="truck" />
          Move Snapshot
        </div>
        <div className="panel-body" style={{ maxHeight: "none" }}>
          <div className="field-row">
            <ReadField label="Move date">{fmtDate(job.moveDate)}</ReadField>
            <ReadField label="Start time">{job.startTime || "—"}</ReadField>
          </div>
          <ReadField label="Total cubic feet">{job.totalCubicFeet || "—"}</ReadField>
          {crewEst ? (
            <div
              className="field"
              style={{
                background: "var(--primary-soft)",
                borderColor: "var(--primary-light)",
              }}
            >
              <label>Suggested crew size (estimate — confirm with dispatch)</label>
              <div className="val" style={{ fontWeight: 700 }}>
                {crewEst}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <Icon name="alert" />
          Access &amp; Building Requirements
        </div>
        <div className="panel-body" style={{ maxHeight: "none" }}>
          <div
            style={{
              fontSize: "10.5px",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: ".03em",
              color: "var(--ink-500)",
              margin: "2px 0 6px",
            }}
          >
            Pickup
          </div>
          <div className="field-row">
            <ReadField label="Stairs">
              {job.stairsAtPickup ? s2("stairsFlights") || "Yes" : "No"}
            </ReadField>
            <ReadField label="Elevator">
              {job.elevatorAtPickup ? job.elevatorRestriction || "Yes" : "No"}
            </ReadField>
          </div>
          <div className="field-row">
            <div className="field">
              <label>COI</label>
              <div className="val">
                <CoiBadge completed={job.coiCompletedPickup} required={job.coiRequiredPickup !== false} />
              </div>
            </div>
            <ReadField label="Long carry">{s2("longCarryPickup") || "—"}</ReadField>
          </div>
          {s2("pickupMasonite") ? (
            <ReadField label="Masonite required">{s2("pickupMasonite")}</ReadField>
          ) : null}

          <div
            style={{
              fontSize: "10.5px",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: ".03em",
              color: "var(--ink-500)",
              margin: "10px 0 6px",
            }}
          >
            Delivery
          </div>
          <div className="field-row">
            <ReadField label="Stairs">
              {job.stairsAtDelivery ? s2("stairsFlightsDelivery") || "Yes" : "No"}
            </ReadField>
            <ReadField label="Elevator">
              {job.elevatorAtDelivery ? job.elevatorRestrictionDelivery || "Yes" : "No"}
            </ReadField>
          </div>
          <div className="field-row">
            <div className="field">
              <label>COI</label>
              <div className="val">
                <CoiBadge completed={job.coiCompletedDelivery} required={job.coiRequiredDelivery !== false} />
              </div>
            </div>
            <ReadField label="Long carry">{s2("longCarryDelivery") || "—"}</ReadField>
          </div>
          {s2("deliveryMasonite") ? (
            <ReadField label="Masonite required">{s2("deliveryMasonite")}</ReadField>
          ) : null}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <Icon name="check" />
          Special Services Confirmed
        </div>
        <div className="panel-body" style={{ maxHeight: "none" }}>
          <div className="field-row">
            <div className="field">
              <label>Carpenter</label>
              <div className="val">
                <ZohoBadgeVal included={job.carpenterIncluded} />
              </div>
            </div>
            <div className="field">
              <label>Assembly</label>
              <div className="val">
                <ZohoBadgeVal included={job.assemblyIncluded} />
              </div>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Disassembly</label>
              <div className="val">
                <ZohoBadgeVal included={job.disassemblyIncluded} />
              </div>
            </div>
            <div className="field">
              <label>TV service</label>
              <div className="val">
                <ZohoBadgeVal included={job.tvServiceIncluded} />
              </div>
            </div>
          </div>
          <div className="field">
            <label>Disposal service</label>
            <div className="val">
              <ZohoBadgeVal included={job.disposalIncluded} />
            </div>
          </div>
          {s2("specialServicesNotes") ? (
            <ReadField label="Special service notes">
              {s2("specialServicesNotes")}
            </ReadField>
          ) : null}
        </div>
      </div>

      {job.riskFlags && job.riskFlags.length ? (
        <div className="panel" style={{ marginBottom: 12, borderColor: "#f3c6c6" }}>
          <div className="panel-head" style={{ color: "var(--danger)" }}>
            <Icon name="alert" />
            Flagged During Confirmation Call
          </div>
          <div className="panel-body" style={{ maxHeight: "none" }}>
            {job.riskFlags.map((r) => {
              const note = s2(RISK_NOTE_KEYS[r]);
              return (
                <div
                  className="field"
                  key={r}
                  style={{
                    borderColor: "var(--danger)",
                    background: "var(--danger-light)",
                  }}
                >
                  <label>{r}</label>
                  {note ? (
                    <div className="val" style={{ fontWeight: 500 }}>
                      {note}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className="dest-card"
          style={{
            background: "linear-gradient(135deg, var(--success-light) 0%, #fff 60%)",
            borderColor: "#bfe6c9",
            marginBottom: 12,
          }}
        >
          <div className="dest-card-head" style={{ marginBottom: 0 }}>
            <div
              className="dest-card-icon"
              style={{ background: "var(--success)", boxShadow: "none" }}
            >
              <Icon name="check" />
            </div>
            <div>
              <div className="dest-card-label" style={{ color: "var(--success)" }}>
                All Clear
              </div>
              <div className="dest-card-address">
                No risks flagged during the Confirmation Call
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head">
          <Icon name="note" />
          Notes for the Crew
        </div>
        <div className="panel-body" style={{ maxHeight: "none" }}>
          <div className="field">
            {s2("confCrewNotes") || "No specific crew notes captured yet."}
          </div>
        </div>
      </div>

    </>
  );
};

export const TabNotesHub = ({ job, isLD, actions }) => {
  if (isLD) return <NotesFeed job={job} isLD={isLD} actions={actions} />;
  return (
    <>
      <CopyPasteVersion job={job} isLD={isLD} />
      <DispatchBrief job={job} />
      <NotesFeed job={job} isLD={isLD} actions={actions} />
    </>
  );
};
